import React, { createContext, useContext, useCallback, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { format } from 'date-fns';

type CartContextProps = {
	children: React.ReactNode;
}
type ItemData = {
	id: string;
	categoria: string;
	cod_barras: string;
	cod_pro: string;
	comiss_ind: number;
	comiss_ven: number;
	complementos: ComplementoData[];
	descricao: string;
	nome: string;
	obs: string;
	promocao: string;
	qtd: number;
	total: number;
	url_imagem: string;
	valor: number;
}
type ComplementoData = {
	codigo: number;
	grupo: number;
	nome: string;
	descricao: string;
	custo: number;
	valor: number;
	priceFormatted: string;
	qtd_maxima: number;
	qtd_gratis: number;
	qtd: number;
}
type PagamentoData = {
	id: string;
	band_pay: string;
	chave_pay: string;
	currency_pay: string;
	id_pay: number;
	max_parcel: number;
	method_pay: string;
	name_pay: string;
	qtd_parcel: number;
	tipchave_pay: string;
	trocoFormatted: string;
	troco_para: number;
	type_pay: string;
	conf: string;
	url_pay: string;
	vlr_pay: number;
}
type EnderecoData = {
  id: string;
  cep: string;
  uf: string;
  cid: string;
  cidcod: string;
  bai: string;
  endereco: string;
  num: string;
  comp?: string;
  ponto_ref?: string;
  emp_cod: string;
  formatted_address: string;
  location: {
    lat: number;
    lng: number;
  };
}
type CupomDescontoData = {
	ativo: string;
	cupom: string;
	data_vencimento: string;
	descricao: string;
	falta_val_minimo: string;
	id_desconto: string;
	regra_estabelecimento: string;
	regra_forma_pag: string;
	regra_primera_compra: string;
	regra_val_minimo: string;
	type_desc: string;
	type_pay: string;
	val_minimo_formatted: string;
	valor: number;
	valor_formatted: string;
}
type AgendamentoData = {
	data: string;
	hora: string;
}
type OperacaoData = {
	concluido: string;
	custo: string;
	estoque: string;
	id: number;
	indicador: string;
	indicador_desc: string;
	nome: string;
	operacao: string;
	pagto: string;
}
type ClienteData = {
	id: string;
	nome: string;
	doc: string;
	phone: string;
	cid: string;
}
type IndicadorData = {
	id: string;
	nome: string;
	cid: string
	doc: string;
	phone: string;
	comissao: number;
}
type CartData = {
	id_order: number;
	cliente: ClienteData;
	consumidor: boolean;
	indicador: IndicadorData;
	operacao: OperacaoData;
	items: ItemData[];
	pagamentos: PagamentoData[];
	endereco_entrega: EnderecoData;
	retirar: boolean;
	taxa_entrega: number;
	agendamento: AgendamentoData;
	agendar: boolean;
	total: number;
	subTotal: number;
	troco: number;
	merchant_id: string;
	cupom: CupomDescontoData;
	campanha: string;
	data_cart: string;
	taxa_service: number;
	falta: number;
	comiss_ven: number;
	prev_entrega: string;
	vendedor: {
		id: string;
		nome: string;
	}
}
interface CartState {
	cart: CartData;
}
interface CartContextData {
	cart: CartData;
	initCart(): void;
	resumeCart(order: CartData): void;
	addCart(item: ItemData, incluir: boolean): void;
	removeCart(id: string): void;
	addCartCliente(cli: ClienteData, consumidor: boolean): void;
	addCartPagamento(pagamento: PagamentoData): void;
	removeCartPagamento(pagamento: PagamentoData): void;
	resetCartPagamento(): void;
	setCartRetirar(): void;
	addCartEndereco(
		endereco: EnderecoData,
		retirar: boolean,
	): void;
	addCartHoraAgendada(agendamento: any, agendar: boolean): void;
	addCartOperacao(operacao: OperacaoData): void;
	addCartMerchant(merchant: string, comissao: number): void;
	addCartIndicador(indicador: IndicadorData): void;
	addCartCupom(cupom: CupomDescontoData): void;
	addCartVendedorPrevEntrega(vendedor: any, prev_entrega: string): void;
}

const CartContext = createContext<CartContextData>({} as CartContextData);

const CartProvider: React.FC<CartContextProps> = ({ children }) => {
	const [cart, setCart] = useState<CartData>({
		id_order: 0,
		cliente: {} as ClienteData,
		consumidor: false,
		indicador: {} as IndicadorData,
		operacao: {} as OperacaoData,
		items: [],
		pagamentos: [],
		endereco_entrega: {} as EnderecoData,
		retirar: false,
		taxa_entrega: 0,
		agendamento: {} as AgendamentoData,
		agendar: false,
		total: 0,
		subTotal: 0,
		troco: 0,
		merchant_id: '',
		cupom: {} as CupomDescontoData,
		campanha: '',
		data_cart: '',
		taxa_service: 0,
		falta: 0,
		comiss_ven: 0,
		prev_entrega: '',
		vendedor: {
			id: '',
			nome: '',
		}
	});

	const [data, setData] = useState<CartState>(() => {
		const cartString = localStorage.getItem('@ControleRazio:cart');

		if (cartString) {
			setCart(JSON.parse(cartString));

			return {
				cart: JSON.parse(cartString)
			};
		}

		return {} as CartState;
	});

	const initCart = useCallback(() => {
		const init = {
			id_order: 0,
			cliente: {} as ClienteData,
			consumidor: false,
			indicador: {} as IndicadorData,
			operacao: {} as OperacaoData,
			items: [],
			pagamentos: [],
			endereco_entrega: {} as EnderecoData,
			retirar: false,
			taxa_entrega: 0,
			agendamento: {} as AgendamentoData,
			agendar: false,
			total: 0,
			subTotal: 0,
			troco: 0,
			merchant_id: '',
			cupom: {} as CupomDescontoData,
			campanha: '',
			data_cart: '',
			taxa_service: 0,
			falta: 0,
			comiss_ven: 0,
			prev_entrega: '',
			vendedor: {
				id: '',
				nome: '',
			}
		};

		setCart(init);
		setData({
			cart: init,
		});

		localStorage.setItem('@ControleRazio:cart', JSON.stringify(init));
	}, []);

	const resumeCart = useCallback((order: CartData) => {
		setCart(order);
		setData({
			cart: order,
		});

		localStorage.setItem('@ControleRazio:cart', JSON.stringify(order));
	}, []);

	const addCart = useCallback(
		(product: ItemData, incluir: boolean) => {
			const {
				id,
				categoria,
				qtd,
				cod_pro,
				comiss_ind,
				comiss_ven,
				cod_barras,
				nome,
				descricao,
				obs,
				valor,
				total,
				promocao,
				url_imagem,
				complementos,
			} = product;

			if (incluir) {
				const uid = uuid();

				const item: ItemData = {
					id: uid,
					qtd,
					categoria,
					cod_pro,
					comiss_ind,
					comiss_ven,
					cod_barras,
					nome,
					descricao,
					obs,
					valor,
					total,
					promocao,
					url_imagem,
					complementos,
				};

				const auxCart = cart;

				// se já existir algum item no carrinho
				if (auxCart.items.length > 0) {
					auxCart.subTotal += total;
					auxCart.total = auxCart.subTotal + auxCart.taxa_entrega;

					// se existir cupom de desconto
					if (auxCart.cupom.id_desconto) {
						auxCart.total -= auxCart.cupom.valor;
					}
				} else {
					// se for o primeiro item do carrinho seta a data do carrinho;
					auxCart.data_cart = format(new Date(), 'Pp'); //String(new Date());

					auxCart.subTotal += total;
					auxCart.total = auxCart.subTotal + auxCart.taxa_entrega;
				}

				auxCart.subTotal = parseFloat(auxCart.subTotal.toFixed(2));
				auxCart.total = parseFloat(auxCart.total.toFixed(2));
				auxCart.falta = auxCart.total;

				auxCart.items.push(item);

				setCart(auxCart);
				setData({
					cart: auxCart
				});

				localStorage.setItem('@ControleRazio:cart', JSON.stringify(auxCart));
			} else {
				const auxCart = cart;
				const indexItem = auxCart.items.findIndex(item => item.id === id);

				auxCart.items[indexItem] = product;
				let auxTotal = 0;

				auxCart.items.map(item => {
					auxTotal += item.total;

					return item;
				});

				auxCart.subTotal = auxTotal;

				if (auxCart.cupom.id_desconto) {
					auxCart.total = auxTotal + cart.taxa_entrega - auxCart.cupom.valor;
				} else {
					auxCart.total = auxTotal + cart.taxa_entrega;
				}

				auxCart.subTotal = parseFloat(auxCart.subTotal.toFixed(2));
				auxCart.total = parseFloat(auxCart.total.toFixed(2));
				auxCart.falta = auxCart.total;

				setCart(auxCart);
				setData({
					cart: auxCart
				});

				localStorage.setItem('@ControleRazio:cart', JSON.stringify(auxCart));
			}
	}, [cart]);

	const removeCart = useCallback(
		(id: string) => {
			const auxCart = cart;
			const indexItem = auxCart.items.findIndex(item => item.id === id);
			const itemExclude = auxCart.items[indexItem].total;
			auxCart.items.splice(indexItem, 1);

			// if (auxCart.cupom.id_desconto) {}

			if (auxCart.items.length === 0) {
				auxCart.total = 0;
				auxCart.subTotal = 0;
				auxCart.cupom = {} as CupomDescontoData;
			} else {
				auxCart.subTotal -= itemExclude;
				auxCart.total = auxCart.subTotal + auxCart.taxa_entrega;

				auxCart.subTotal = parseFloat(auxCart.subTotal.toFixed(2));
				auxCart.total = parseFloat(auxCart.total.toFixed(2));
			}

			setCart(auxCart);

			setData({
				cart: auxCart
			});

			localStorage.setItem('@ControleRazio:cart', JSON.stringify(auxCart));
	}, [cart]);

	const addCartCliente = useCallback((cli: ClienteData, consumidor: boolean) => {
		const auxCart = { ...cart, consumidor, cliente: cli };

		setCart({ ...cart, consumidor, cliente: cli });
		setData({
			cart: auxCart
		});

		localStorage.setItem('@ControleRazio:cart', JSON.stringify(auxCart));
	}, [cart]);

	const addCartPagamento = useCallback((pagamento: PagamentoData) => {
			const auxCart = cart;
			const auxPay = { ...pagamento, id: uuid() }
			auxCart.pagamentos = [ ...auxCart.pagamentos, auxPay];

			let val = 0;

			auxCart.pagamentos.forEach(pay => {
				val += pay.vlr_pay;
			});
			if (val > auxCart.total) {
				auxCart.troco = Number((val - auxCart.total).toFixed(2));
				auxCart.falta = 0;
			} else {
				auxCart.troco = 0;
				auxCart.falta = Number((auxCart.total - val).toFixed(2));
			}

			setCart(auxCart);
			setData({
				cart: auxCart
			});

			localStorage.setItem('@ControleRazio:cart', JSON.stringify(auxCart));
	}, [cart]);

	const removeCartPagamento = useCallback(
		(pagamento: PagamentoData) => {
			const auxCart = cart;
			const indexPay = auxCart.pagamentos.findIndex(pay => pay.id === pagamento.id);

			auxCart.pagamentos.splice(indexPay, 1);

			let val = 0;

			auxCart.pagamentos.forEach(pay => {
				val += pay.vlr_pay;
			});

			if (val > auxCart.total) {
				auxCart.troco = val - auxCart.total;
				auxCart.falta = 0;
			} else {
				auxCart.troco = 0;
				auxCart.falta = Number((auxCart.total - val).toFixed(2));
			}

			setCart(auxCart);
			setData({
				cart: auxCart
			});

			localStorage.setItem('@ControleRazio:cart', JSON.stringify(auxCart));
	}, [cart]);

	const resetCartPagamento = useCallback(() => {
		const auxCart = {
			...cart,
			troco: 0,
			falta: cart.total,
			pagamentos: []
		};

		setCart(auxCart);
		setData({
			cart: auxCart
		});

		localStorage.setItem('@ControleRazio:cart', JSON.stringify(auxCart));
	}, [cart]);

	const setCartRetirar = useCallback(() => {
		const auxCart = cart;
		auxCart.retirar = !auxCart.retirar;

		setCart({ ...cart, retirar: !auxCart.retirar});
		setData({
			cart: { ...cart, retirar: !auxCart.retirar },
		});

		localStorage.setItem('@ControleRazio:cart', JSON.stringify(auxCart));
	}, [cart]);

	const addCartEndereco = useCallback(async (endereco: EnderecoData, retirar: boolean) => {
		const auxCart = cart;
		auxCart.endereco_entrega = endereco;
		auxCart.retirar = retirar;

			setCart(auxCart);
			setData({
				cart: auxCart
			});

		localStorage.setItem('@ControleRazio:cart', JSON.stringify(auxCart));
	} , [cart]);

	const addCartHoraAgendada = useCallback((agendamento: any, agendar: boolean) => {
		const auxCart = cart;
		auxCart.agendar = agendar;
		auxCart.agendamento = agendamento;

		setCart(auxCart);
		setData({
			cart: auxCart
		});

		localStorage.setItem('@ControleRazio:cart', JSON.stringify(auxCart));
	}, [cart]);

	const addCartOperacao = useCallback(async (operacao: OperacaoData) => {
		const  auxCart = cart;
		auxCart.operacao = operacao;
		auxCart.troco = operacao.pagto === 'N' ? 0 : cart.troco;
		auxCart.falta = operacao.pagto === 'N' ? cart.total : cart.falta;
		auxCart.pagamentos = operacao.pagto === 'N' ? [] : cart.pagamentos;
		auxCart.indicador = operacao.indicador === 'N' ? {} as IndicadorData : cart.indicador;

		setCart(auxCart);
		setData({
			cart: auxCart
		});

		localStorage.setItem('@ControleRazio:cart', JSON.stringify(auxCart));
	}, [cart]);

	const addCartMerchant = useCallback((merchant: string, comissao: number) => {
		const auxCart = {
			...cart,
			merchant_id: merchant,
			comiss_ven: comissao
		};

		setCart(auxCart);
		setData({
			cart: auxCart
		});

		localStorage.setItem('@ControleRazio:cart', JSON.stringify(auxCart));
	}, [cart]);

	const addCartIndicador = useCallback((indicador: IndicadorData) => {
		const auxCart = { ...cart, indicador: indicador };

		setCart(auxCart);
		setData({
			cart: auxCart
		});

		localStorage.setItem('@ControleRazio:cart', JSON.stringify(auxCart));
	}, [cart]);

	const addCartCupom = useCallback(
		(cupom: CupomDescontoData) => {
			const auxCart = cart;

			if (cupom.id_desconto) {
				auxCart.cupom = cupom;
				auxCart.total = auxCart.subTotal + auxCart.taxa_entrega;
				auxCart.total -= cupom.valor;
			} else {
				auxCart.cupom = cupom;
				auxCart.total = auxCart.subTotal + auxCart.taxa_entrega;
			}

			auxCart.total = parseFloat(auxCart.total.toFixed(2));

			setCart(auxCart);
			setData({
				cart: auxCart
			});

			localStorage.setItem('@ControleRazio:cart', JSON.stringify(auxCart));
	}, [cart]);

	const addCartVendedorPrevEntrega = useCallback((vendedor: any, prev_entrega: string) => {
		const auxCart = cart;
		auxCart.vendedor = vendedor;
		auxCart.prev_entrega = prev_entrega;

		setCart(auxCart);
		setData({
			cart: auxCart
		});

		localStorage.setItem('@ControleRazio:cart', JSON.stringify(auxCart));
	}, [cart]);

	return (
		<CartContext.Provider
			value={{
				cart: data.cart,
				initCart,
				resumeCart,
				addCart,
				removeCart,
				addCartCliente,
				addCartPagamento,
				removeCartPagamento,
				resetCartPagamento,
				setCartRetirar,
				addCartEndereco,
				addCartHoraAgendada,
				addCartOperacao,
				addCartMerchant,
				addCartIndicador,
				addCartCupom,
				addCartVendedorPrevEntrega,
			}}
		>
			{children}
		</CartContext.Provider>
	);
};

function useCart(): CartContextData {
	const context = useContext(CartContext);

	if (!context) {
		throw new Error('useAuth must be used within an AuthProvider');
	}

	return context;
}

export { CartProvider, useCart };
